import { useQuery } from "@tanstack/react-query";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import api from "@/lib/api";
import { num } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Loader2 } from "lucide-react";

const MESI = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];
const label = (k) => `${MESI[Number(k.slice(5, 7)) - 1]} ${k.slice(2, 4)}`;
const COLS_BY = {
  ig: [["ig_reach", "Raggiunti*"], ["ig_views", "Visualizzazioni"], ["ig_profile_views", "Visite profilo"], ["ig_total_interactions", "Interazioni"], ["ig_website_clicks", "Clic sito"], ["posts", "Post"]],
  fb: [["fb_page_total_media_view_unique", "Raggiunti*"], ["fb_page_media_view", "Visualizzazioni"], ["fb_page_views_total", "Visite pagina"], ["fb_page_post_engagements", "Interazioni"]],
};
const sum = (rows, k) => rows.reduce((s, r) => s + (r[k] || 0), 0);

const YoY = ({ cur, prev, COLS }) => {
  if (!prev.length) return null;
  return (
    <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="social-yoy">
      {COLS.slice(0, 4).map(([k, l]) => {
        const a = sum(cur, k), b = sum(prev, k), d = b ? Math.round(((a - b) / b) * 100) : null;
        return (
          <Card key={k} className="p-4">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{l.replace("*", "")} · 12 mesi</p>
            <p className="font-display text-2xl font-semibold">{num(a)}</p>
            <span className={`text-[11px] ${d == null ? "text-muted-foreground" : d >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{d == null ? "nessun dato anno prima" : `${d >= 0 ? "+" : ""}${d}% vs 12 mesi prima`}</span>
          </Card>
        );
      })}
    </div>
  );
};

export const SocialMonthly = ({ months, source = "ig" }) => {
  const COLS = COLS_BY[source];
  const key = COLS[0][0];
  const { data } = useQuery({ queryKey: ["social", "monthly", months], queryFn: async () => (await api.get("/social/monthly", { params: { months } })).data });
  if (!data) return <Loader2 className="h-6 w-6 animate-spin text-primary" />;
  const chart = data.months.map((r, i) => ({ ...r, label: label(r.month), cur: r[key], prev: data.previous_year[i]?.[key] }));
  return (
    <div data-testid="social-monthly">
      <YoY cur={data.months} prev={data.previous_year} COLS={COLS} />
      <Card className="mb-6 p-5">
        <p className="mb-3 font-mono-erp text-[11px] uppercase tracking-widest text-muted-foreground">Account raggiunti su {source === "ig" ? "Instagram" : "Facebook"} mese per mese{data.previous_year.length ? " · linea: stesso mese anno prima" : ""}</p>
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={chart}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
            <XAxis dataKey="label" fontSize={11} />
            <YAxis fontSize={11} width={50} />
            <Tooltip formatter={(v, n) => [num(v), n === "prev" ? "Anno prima" : "Raggiunti"]} />
            <Legend formatter={(n) => (n === "prev" ? "Anno prima" : "Raggiunti")} />
            <Bar dataKey="cur" fill="hsl(var(--primary))" radius={[3, 3, 0, 0]} />
            {data.previous_year.length > 0 && <Line dataKey="prev" stroke="hsl(var(--muted-foreground))" strokeDasharray="4 4" dot={false} />}
          </ComposedChart>
        </ResponsiveContainer>
      </Card>
      <Card className="mb-6 overflow-x-auto p-5" data-testid="social-monthly-table">
        <table className="w-full min-w-[720px] text-sm">
          <thead><tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground"><th className="pb-2">Mese</th>{COLS.map(([k, l]) => <th key={k} className="pb-2 text-right">{l}</th>)}</tr></thead>
          <tbody>
            {[...data.months].reverse().map((r) => (
              <tr key={r.month} className="border-t border-border/40" data-testid={`social-month-${r.month}`}>
                <td className="py-1.5">{label(r.month)}</td>
                {COLS.map(([k]) => <td key={k} className="py-1.5 text-right">{r[k] == null ? "—" : num(r[k])}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-2 text-[11px] text-muted-foreground">* Meta dà i totali al massimo per 30 giorni: i raggiunti del mese sono la somma di due metà, quindi leggermente sovrastimati. Visualizzazioni e interazioni sono esatte.</p>
      </Card>
    </div>
  );
};
